import { cn } from '@/lib/cn';
import type { Board, Row } from '@/lib/qcp/protocol';
import { OfcRow } from './OfcRow';
import { rowToLabel } from './rowUtils';

interface OfcBoardProps {
  board: Board;
  capacities?: { front: 3; middle: 5; back: 5 };
  /**
   * Called when a slot is tapped: `(row, slotIndex)`. Enables both placing
   * (tap an empty slot / a row with a card selected) and un-placing (tap a
   * pending card to send it back to the tray). Per-slot so the caller can tell
   * which card was tapped.
   */
  onSlotTap?: (row: Row, index: number) => void;
  /** Keys (`${r}${s}`) of cards placed THIS hand — dim ring. */
  pendingKeys?: Set<string>;
  /** Key of the picked-up pending card — bright ring (selected for move/remove). */
  pickedKey?: string;
  /** Rows that should glow as legal placement targets. */
  highlightRows?: Row[];
  /**
   * Compact layout for the opponent's board: smaller overall, no slot-tap
   * interactions, face-down cards if the board is hidden (not used here —
   * the server already masks opponent private state; compact is a layout hint).
   */
  compact?: boolean;
}

/**
 * Full OFC board — three rows stacked front (3) → middle (5) → back (5).
 *
 * In normal mode: rows are tappable (onRowTap fires for the whole row, not
 * individual slots) and highlight ring is applied when the row is in
 * `highlightRows`.
 *
 * In `compact` mode (opponent board): no tap handlers, smaller container.
 */
export function OfcBoard({
  board,
  capacities = { front: 3, middle: 5, back: 5 },
  onSlotTap,
  pendingKeys,
  pickedKey,
  highlightRows = [],
  compact = false,
}: OfcBoardProps) {
  const rows: { row: Row; cards: typeof board.front; capacity: 3 | 5 }[] = [
    { row: 'front',  cards: board.front,  capacity: capacities.front  },
    { row: 'middle', cards: board.middle, capacity: capacities.middle },
    { row: 'back',   cards: board.back,   capacity: capacities.back   },
  ];

  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-xl border border-border bg-bg-secondary p-2',
        compact && 'scale-[0.82] origin-top',
      )}
    >
      {rows.map(({ row, cards, capacity }) => (
        <OfcRow
          key={row}
          label={rowToLabel(row)}
          cards={cards}
          capacity={capacity}
          highlight={highlightRows.includes(row) && !compact}
          onSlotTap={!compact && onSlotTap ? (idx) => onSlotTap(row, idx) : undefined}
          pendingKeys={compact ? undefined : pendingKeys}
          pickedKey={compact ? undefined : pickedKey}
        />
      ))}
    </div>
  );
}
